import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import styles from "./CalibrationRequestForm.module.scss";
import Axios from "../../_services/caller.service";
import { useCabineContext } from "../../context/CabineContext";
import CalibrationRequestDetailsForm from "./CalibrationRequestDetailsForm";
import {
  HasEarsStep,
  SuccessStep,
  TransducerStep,
  TutorialStep,
} from "./CalibrationRequestFlowSteps";
import CalibrationRequestRecap from "./CalibrationRequestRecap";

const STEPS = {
  TRANSDUCERS: "transducers",
  HAS_EARS: "has_ears",
  TUTORIAL: "tutorial",
  FORM: "form",
  RECAP: "recap",
  SUCCESS: "success",
};

// Ajoutez ici les comptes pour lesquels le nom et l'email ne doivent pas etre pre-remplis.
const CONTACT_PREFILL_EXCEPTIONS = [
  {
    displayName: "Nicolas Dezaire",
    email: "nicolas.dezaire@vyv3.fr",
  },
];

const normalizeForCompare = (value) => String(value || "").trim().toLowerCase();

const shouldSkipContactPrefill = (ownerProfile) =>
  CONTACT_PREFILL_EXCEPTIONS.some(
    ({ displayName, email }) =>
      normalizeForCompare(ownerProfile?.display_name) ===
        normalizeForCompare(displayName) &&
      normalizeForCompare(ownerProfile?.user_email) === normalizeForCompare(email)
  );

const CalibrationRequestForm = ({ ownerProfile, ownerId, onClose }) => {
  const { t } = useTranslation();
  const { cabine } = useCabineContext();
  const skipContactPrefill = shouldSkipContactPrefill(ownerProfile);

  // Nouveau flow unifie: on commence toujours par le choix des transducteurs
  const [step, setStep] = useState(STEPS.TRANSDUCERS);
  const [selectedTransducers, setSelectedTransducers] = useState([]);
  const [effectiveType, setEffectiveType] = useState(null);
  const [formData, setFormData] = useState({
    name: skipContactPrefill ? "" : ownerProfile?.display_name || "",
    email: skipContactPrefill ? "" : ownerProfile?.user_email || "",
    phone: ownerProfile?.phone || "",
    cabineId: "",
    centerName: "",
    address: "",
    address2: "",
    zipCode: "",
    city: "",
    country: "",
    message: "",
    serialDragonfly: "",
    noDragonfly: false,
    serialCO: "",
    noCO: false,
  });
  const [loading, setLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [error, setError] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const fetchCabineAddress = useCallback(async (cabId) => {
    if (!cabId) return;
    setAddressLoading(true);
    try {
      const res = await Axios.get(`/calibrationRequest/address/${cabId}`);
      if (!res.data.error && res.data.data) {
        const { address, address2, zipCode, city, country, phone } = res.data.data;
        setFormData((prev) => ({
          ...prev,
          address: address || "",
          address2: address2 || "",
          zipCode: zipCode || "",
          city: city || "",
          country: country || "",
          ...(phone ? { phone } : {}),
        }));
      }
    } catch (err) {
      // Pas bloquant, l'utilisateur peut remplir manuellement.
    } finally {
      setAddressLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.cabineId) {
      fetchCabineAddress(formData.cabineId);
    }
  }, [formData.cabineId, fetchCabineAddress]);

  useEffect(() => {
    const selectedCabineForName = cabine?.find(
      (c) => String(c.id) === String(formData.cabineId)
    );

    if (selectedCabineForName) {
      setFormData((prev) => ({
        ...prev,
        centerName: selectedCabineForName.cabname || "",
      }));
    }
  }, [cabine, formData.cabineId]);

  const selectedCabine = cabine?.find(
    (c) => String(c.id) === String(formData.cabineId)
  );

  const isBone = effectiveType === "bone_conduction";
  const isHeadphonesCalibration = effectiveType === "headphones_calibration";
  const isInsertsCalibration = effectiveType === "inserts_calibration";
  const isArtificialEarShipment =
    isHeadphonesCalibration || isInsertsCalibration;
  const isPostalCalibration = isBone;
  const isEarsReceive = effectiveType === "ears_receive";
  const isEarsReturn = effectiveType === "ears_return";
  const isEars = isEarsReceive || isEarsReturn;
  const shouldProvideUserPdf = !isEarsReceive && !isArtificialEarShipment;
  const selectedTransducer = selectedTransducers[0];

  const buildPayload = (previewOnly) => ({
    type: effectiveType,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    cabineName:
      formData.centerName ||
      selectedCabine?.cabname ||
      `Cabine ${formData.cabineId}`,
    address: [formData.address, formData.address2].filter(Boolean).join(", "),
    zipCode: formData.zipCode,
    city: formData.city,
    country: formData.country,
    message: formData.message,
    ...(isBone && {
      serialDragonfly: formData.noDragonfly
        ? t("calibration_request.serial_unavailable")
        : formData.serialDragonfly,
      serialCO: formData.noCO
        ? t("calibration_request.serial_unavailable")
        : formData.serialCO,
    }),
    previewOnly,
  });

  const goToRecap = (e) => {
    e.preventDefault();
    setStep(STEPS.RECAP);
  };

  const getDownloadFilename = () => {
    if (isBone) return "Demande_de_calibration_CO.pdf";
    if (isHeadphonesCalibration) {
      return "Demande_envoi_oreille_artificielle_casque.pdf";
    }
    if (isInsertsCalibration) {
      return "Demande_envoi_oreille_artificielle_inserts.pdf";
    }
    if (isEarsReceive) return "Demande_envoi_oreilles_artificielles.pdf";
    return "Demande_retour_oreilles_artificielles.pdf";
  };

  const handleDownloadPdf = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Axios.post(
        `/calibrationRequest/${ownerId}`,
        buildPayload(true),
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      setPdfBlob(blob);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getDownloadFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("calibration_request.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Axios.post(
        `/calibrationRequest/${ownerId}`,
        buildPayload(false),
        { responseType: "blob" }
      );
      if (shouldProvideUserPdf && !pdfBlob) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = getDownloadFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setError(t("calibration_request.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleTransducerChange = (value) => {
    setSelectedTransducers([value]);
    setEffectiveType(null);
  };

  const handleTransducerNext = () => {
    const selected = selectedTransducers[0];

    if (selected === "bone_conduction") {
      setEffectiveType("bone_conduction");
      setStep(STEPS.FORM);
      return;
    }

    if (selected === "headphones" || selected === "inserts") {
      setStep(STEPS.HAS_EARS);
      return;
    }

    if (selected === "free_field") {
      setStep(STEPS.TUTORIAL);
    }
  };

  const handleMissingArtificialEars = () => {
    setEffectiveType(
      selectedTransducer === "headphones"
        ? "headphones_calibration"
        : "inserts_calibration"
    );
    setStep(STEPS.FORM);
  };

  const handleBack = () => {
    switch (step) {
      case STEPS.TRANSDUCERS:
        onClose();
        break;
      case STEPS.HAS_EARS:
        setStep(STEPS.TRANSDUCERS);
        break;
      case STEPS.TUTORIAL:
        setStep(
          selectedTransducer === "headphones" || selectedTransducer === "inserts"
            ? STEPS.HAS_EARS
            : STEPS.TRANSDUCERS
        );
        break;
      case STEPS.FORM:
        if (isHeadphonesCalibration || isInsertsCalibration || isEarsReceive) {
          setStep(STEPS.HAS_EARS);
          break;
        }
        setStep(STEPS.TRANSDUCERS);
        break;
      case STEPS.RECAP:
        setStep(STEPS.FORM);
        break;
      default:
        onClose();
    }
  };

  const formTitleKey = isBone
    ? "calibration_request.form_title_bone"
    : isHeadphonesCalibration
    ? "calibration_request.form_title_headphones_calibration"
    : isInsertsCalibration
    ? "calibration_request.form_title_inserts_calibration"
    : isEarsReceive
    ? "calibration_request.form_title_ears_receive"
    : "calibration_request.form_title_ears_return";

  const formDescKey = isBone
    ? "calibration_request.desc_bone"
    : isHeadphonesCalibration
    ? "calibration_request.desc_headphones_calibration"
    : isInsertsCalibration
    ? "calibration_request.desc_inserts_calibration"
    : isEarsReceive
    ? "calibration_request.desc_ears_receive"
    : "calibration_request.desc_ears_return";

  return (
    <div className={styles.pageContainer}>
      {step !== STEPS.SUCCESS && (
        <button className={styles.backToMenu} onClick={handleBack} type="button">
          <FaArrowLeft /> {t("calibration_request.back")}
        </button>
      )}

      {step === STEPS.TRANSDUCERS && (
        <TransducerStep
          t={t}
          selectedTransducers={selectedTransducers}
          onTransducerChange={handleTransducerChange}
          onNext={handleTransducerNext}
        />
      )}

      {step === STEPS.HAS_EARS && (
        <HasEarsStep
          t={t}
          onHasEars={() => setStep(STEPS.TUTORIAL)}
          onMissingArtificialEars={handleMissingArtificialEars}
        />
      )}

      {step === STEPS.TUTORIAL && (
        <TutorialStep
          t={t}
          selectedTransducers={selectedTransducers}
          onClose={onClose}
        />
      )}

      {step === STEPS.FORM && (
        <CalibrationRequestDetailsForm
          t={t}
          cabine={cabine}
          formData={formData}
          formTitleKey={formTitleKey}
          formDescKey={formDescKey}
          addressLoading={addressLoading}
          isBone={isBone}
          isEars={isEars}
          isHeadphonesCalibration={isHeadphonesCalibration}
          isInsertsCalibration={isInsertsCalibration}
          onChange={handleChange}
          onSubmit={goToRecap}
        />
      )}

      {step === STEPS.RECAP && (
        <CalibrationRequestRecap
          t={t}
          formData={formData}
          selectedCabine={selectedCabine}
          isBone={isBone}
          isPostalCalibration={isPostalCalibration}
          isEars={isEars}
          isEarsReceive={isEarsReceive}
          isArtificialEarShipment={isArtificialEarShipment}
          shouldProvideUserPdf={shouldProvideUserPdf}
          loading={loading}
          error={error}
          onDownloadPdf={handleDownloadPdf}
          onConfirmSend={handleConfirmSend}
        />
      )}

      {step === STEPS.SUCCESS && <SuccessStep t={t} onClose={onClose} />}
    </div>
  );
};

export default CalibrationRequestForm;
